// Page publique de saisie d'IBAN : un participant dont le remboursement a échoué arrive ici depuis
// le lien personnel reçu par email (dossier de remboursement, onglet Billetterie de la fiche event).
// Aucune authentification ; le lien sert une fois et n'affiche que le prénom, l'event et le montant.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Paper, Box, Stack, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { CheckCircle2 } from 'lucide-react';
import { getRefundBankLink, submitRefundBankLink } from '../../api/api';
import PublicFormHeader from './PublicFormHeader';
import { BRAND_GRADIENT } from './formVisuals';

// Même contrôle que l'API (ISO 13616, clé mod 97) : signale une faute de frappe avant l'envoi.
const normalizeIban = (iban) => iban.replace(/\s+/g, '').toUpperCase();
export const isValidIban = (iban) => {
  const value = normalizeIban(iban);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(value)) return false;
  let remainder = 0;
  for (const char of value.slice(4) + value.slice(0, 4)) {
    const digits = /\d/.test(char) ? char : String(char.charCodeAt(0) - 55);
    for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97;
  }
  return remainder === 1;
};

// Affichage par blocs de 4, comme sur un RIB.
const formatIban = (iban) => normalizeIban(iban).replace(/(.{4})(?=.)/g, '$1 ');

const PageShell = ({ eventName, children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
    <PublicFormHeader projectName={eventName} maxWidth={560} />
    <Container sx={{ py: { xs: 4, sm: 8 }, maxWidth: '560px !important' }}>{children}</Container>
  </Box>
);

const Submitted = ({ eventName }) => (
  <PageShell eventName={eventName}>
    <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
      <Box sx={{ color: '#007739', mb: 1.5 }}><CheckCircle2 size={40} /></Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Coordonnées bien reçues</Typography>
      <Typography variant="body2" color="text.secondary">
        Merci. Notre équipe procède à votre remboursement par virement. Pour toute question, répondez à l'email que vous avez reçu.
      </Typography>
    </Paper>
  </PageShell>
);

const RefundBankDetailsPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ titulaire: '', iban: '', bic: '' });
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getRefundBankLink(token);
        if (!cancelled) setView(data);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.response?.status === 404
            ? "Ce lien n'est plus valide : il a expiré, ou un lien plus récent vous a été envoyé. Répondez à l'email reçu pour en obtenir un nouveau."
            : 'Erreur lors du chargement de la page. Réessayez dans quelques instants.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <PageShell>
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
      </PageShell>
    );
  }
  if (loadError) return <PageShell><Alert severity="error">{loadError}</Alert></PageShell>;
  if (view.state === 'submitted') return <Submitted eventName={view.eventName} />;

  const ibanError = touched && form.iban && !isValidIban(form.iban)
    ? 'IBAN invalide : vérifiez chaque caractère (la clé de contrôle ne correspond pas).'
    : '';
  const titulaireOk = form.titulaire.trim().length >= 2;
  const canSubmit = titulaireOk && isValidIban(form.iban) && !submitting;
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitRefundBankLink(token, {
        titulaire: form.titulaire.trim(),
        iban: normalizeIban(form.iban),
        ...(form.bic.trim() && { bic: form.bic.trim().toUpperCase() }),
      });
      setView({ state: 'submitted', eventName: view.eventName });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) setView({ state: 'submitted', eventName: view.eventName });
      else if (status === 404) setLoadError("Ce lien n'est plus valide. Répondez à l'email reçu pour en obtenir un nouveau.");
      else setSubmitError(err?.response?.data?.message || err?.response?.data?.error || "L'envoi a échoué. Réessayez dans quelques instants.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell eventName={view.eventName}>
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Remboursement · {view.eventName}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {view.prenom ? `Bonjour ${view.prenom},` : 'Bonjour,'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Nous n'avons pas pu vous rembourser automatiquement. Indiquez le compte sur lequel vous souhaitez recevoir
          le virement de <strong>{view.amountLabel}</strong>.
        </Typography>

        <Stack component="form" spacing={2.25} onSubmit={handleSubmit} noValidate>
          <TextField
            label="Titulaire du compte"
            value={form.titulaire}
            onChange={set('titulaire')}
            autoComplete="name"
            required
            fullWidth
            error={touched && !titulaireOk}
            helperText={touched && !titulaireOk ? 'Nom et prénom du titulaire, tels que sur le RIB' : ' '}
          />
          <TextField
            label="IBAN"
            value={form.iban}
            onChange={set('iban')}
            onBlur={(e) => { setTouched(true); setForm((f) => ({ ...f, iban: formatIban(e.target.value) })); }}
            placeholder="FR76 1234 5678 9012 3456 7890 123"
            required
            fullWidth
            inputProps={{ autoCapitalize: 'characters', spellCheck: false, style: { fontFamily: 'monospace', letterSpacing: 0.5 } }}
            error={!!ibanError}
            helperText={ibanError || 'Il figure sur votre RIB ou dans votre application bancaire.'}
          />
          <TextField
            label="BIC (facultatif)"
            value={form.bic}
            onChange={set('bic')}
            fullWidth
            inputProps={{ maxLength: 11, autoCapitalize: 'characters', spellCheck: false }}
            helperText="Utile pour un compte hors de France."
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            sx={{ background: BRAND_GRADIENT, fontWeight: 700, py: 1.25 }}
          >
            {submitting ? 'Envoi…' : 'Envoyer mes coordonnées'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            Vos coordonnées sont transmises de façon sécurisée et uniquement utilisées pour ce remboursement.
            Ce lien ne fonctionne qu'une fois.
          </Typography>
        </Stack>
      </Paper>
    </PageShell>
  );
};

export default RefundBankDetailsPage;
